import React, { MouseEvent } from 'react';
import { categoriesToString, getLabelScreenReader, getLabel, getIconForGroupedAssignedPage } from '../utils/Helper';
import { calculatePointOnCircle } from '../utils/MathUtils';
import { Hyperlink } from '../../../../../components/atoms/Hyperlink';
import { ContentParagraph } from '../../../ContentParagraph';
import Btn from '../../../../../components/molecules/Btn';
import { ButtonType } from '../../../../../types/ButtonType';
import { Icons, SVGIcon } from '../../../../../components/atoms/SVG';
import { TooltipPlacement } from '../../../../../components/atoms/Tooltip';
import { IPageDimension } from '../interfaces/IConnectedPagesGraphProperties';
import { ICoordinates } from '../../../../../types/ICoordinates';
import { PageToPageAssignment } from '../../../../../types/api/PageToPageAssignment';
import { GroupedAssignedPage, IConnectedPage } from '../../../../../types/api/connectedPages/IConnectedPage';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { Namespace } from '../../../../../translation/namespace';
import { ellipsis } from '../../../../StringUtils';

interface INodeProperties {
  reference: unknown;
  left: number;
  top: number;
  node: IConnectedPage;
  borderColorFromCategories?: string;
  maxMinDimensions: IPageDimension;
  notCurrentPage: boolean;
  width: number;
  maxNumberOfCharactersForTitleBeforeEllipse: number;
  maxNumberOfCharactersForDescriptionTextBeforeEllipse: number;
  description?: string;
  onCopy: (label: string) => void;
  ariaLabelPrefix: string;
  buttonDimensions: {
    height: number;
    width: number;
  };

  activeCustomActionButton?: string;
  onCustomActionButtonClicked: (
    event: MouseEvent<HTMLButtonElement | HTMLAnchorElement> & { target: { id: string } }
  ) => void;
  groupedAssignedPages?: GroupedAssignedPage[];
}

export function Node(props: INodeProperties) {
  const { t } = useSonTranslation(Namespace.ConnectedPagesGraph);

  const calculateCustomActionButtonCoordinates = (dimensionInPixel: number, index: number): ICoordinates => {
    const halfDimensions = dimensionInPixel / 2;
    const {
      buttonDimensions = {
        height: 40,
        width: 40,
      },
    } = props;

    return calculatePointOnCircle(
      230 + buttonDimensions.height * 0.6 * index,
      { x: halfDimensions, y: halfDimensions },
      halfDimensions,
    );
  };

  const renderCustomActionButton = (
    id: PageToPageAssignment,
    svgSpriteId: SVGIcon,
    name: string,
    coordinates: ICoordinates,
  ) => {
    const {
      buttonDimensions = {
        height: 60,
        width: 60,
      },
    } = props;

    return (
      <li
        key={id}
        style={{ left: coordinates.x, top: coordinates.y }}
        className="connected-pages-graph__button-container"
      >
        <Btn
          cssClass="connected-pages-graph__button"
          svgClass="connected-pages-graph__icon"
          id={id}
          text={name}
          titleText={name}
          href={`#${props.node.pageLink.url}/${id}`}
          hiddenText
          svgSpriteId={props.activeCustomActionButton !== id ? svgSpriteId : Icons.DECLINE_CROSS}
          type={ButtonType.BUTTON}
          onClick={props.onCustomActionButtonClicked}
          ariaControls={`#${props.node.pageLink.url}/${id}`}
          ariaExpanded={props.activeCustomActionButton === id}
          tooltipPlacement={TooltipPlacement.TOP}
          style={{
            /* stylelint-disable value-keyword-case */
            width: buttonDimensions.width,
            height: buttonDimensions.height,
            marginLeft: (buttonDimensions.width / 2) * -1 - 6,
            marginTop: (buttonDimensions.height / 2) * -1 - 6,
            /* stylelint-enable value-keyword-case */
          }}
        />
      </li>
    );
  };

  const renderCustomActionButtonGroup = (dimensionInPixel: number, groupedAssignedPages: GroupedAssignedPage[]) => {
    if (groupedAssignedPages?.length === 0) {
      return null;
    }

    return (
      <ul aria-label={t('connectedPagesSelectionLabel')} className="connected-pages-graph__button-list">
        {groupedAssignedPages.map((element, index) =>
          renderCustomActionButton(
            element.roleId,
            getIconForGroupedAssignedPage(element),
            element.name,
            calculateCustomActionButtonCoordinates(dimensionInPixel, index),
          ),
        )}
      </ul>
    );
  };

  return (
    <div
      className="connected-pages-graph__current-item"
      style={{
        width: props.width,
        height: props.width,
        marginLeft: (props.width / 2) * -1,
        marginTop: (props.width / 2) * -1,
        left: props.left,
        top: props.top,
        /* stylelint-disable value-keyword-case */
        maxWidth: props.maxMinDimensions.maxWidth,
        minWidth: props.maxMinDimensions.minWidth,
        maxHeight: props.maxMinDimensions.maxWidth,
        minHeight: props.maxMinDimensions.minWidth,
        /* stylelint-enable value-keyword-case */
        borderColor: props.borderColorFromCategories,
      }}
    >
      <div className="connected-pages-graph__current-item-content">
        <div className="connected-pages-graph__current-item-content-position-container">
          {props.notCurrentPage
            ? (
                <>
                  {categoriesToString(props.node.categories)}
                  <Hyperlink
                    id={props.node.pageLink.url}
                    className="connected-pages-graph__current-item-link"
                    reference={props.reference}
                    ariaLabel={getLabelScreenReader(
                      props.node.categories, props.node.pageLink.name, props.ariaLabelPrefix)}
                    onCopy={() => props.onCopy(getLabel(props.node.categories, props.node.pageLink.name))}
                    to={`${props.node.pageLink.url}${
                      props.node.childPages && props.node.childPages.length > 0 ? '?mode=graph' : ''
                    }`}
                  >
                    {ellipsis(props.node.pageLink.name, props.maxNumberOfCharactersForTitleBeforeEllipse)}
                  </Hyperlink>
                </>
              )
            : (
                <p
                  className="connected-pages-graph__current-item-headline"
                  aria-label={getLabelScreenReader(
                    props.node.categories, props.node.pageLink.name, props.ariaLabelPrefix)}
                  onCopy={() => props.onCopy(getLabel(props.node.categories, props.node.pageLink.name))}
                >
                  {categoriesToString(props.node.categories)}
                  {ellipsis(props.node.pageLink.name, props.maxNumberOfCharactersForTitleBeforeEllipse)}
                </p>
              )}
          {props.description && (
            <ContentParagraph aria-label={props.description} onCopy={() => props.onCopy(props.description || '')}>
              {ellipsis(props.description, props.maxNumberOfCharactersForDescriptionTextBeforeEllipse)}
            </ContentParagraph>
          )}
        </div>
      </div>
      <div>
        {props.groupedAssignedPages && props.groupedAssignedPages.length > 0
          ? renderCustomActionButtonGroup(props.width, props.groupedAssignedPages)
          : null}
      </div>
    </div>
  );
}
